// Telemetry client - sends user action events to backend telemetry endpoints
// All functions auto-include origin: 'web' and use JWT authentication via apiFetch

import { apiFetch } from './config';
import type {
  HeartbeatIn,
  OnlineUsersOut,
  ErrorIn,
  FavoriteAddIn,
  FavoriteRemoveIn,
  FavoriteListViewIn,
  JobCreatedIn,
  JobViewIn,
  JobDeletedIn,
  JobCorrectedIn,
  ProductClickIn,
  ProductViewIn,
  ScannerStartIn,
  ScannerResultViewIn,
  ScannerCompareIn,
  ScannerPrefsViewIn,
  ScannerPrefsUpdateIn,
  CompatCompareIn,
  CompatPrefsViewIn,
  CompatPrefsUpdateIn,
  WeatherAnalyzeIn,
  UserPlanChangeIn,
  TelemetryOrigin,
} from '../types/telemetry';

export const ORIGIN: TelemetryOrigin = 'web';

// Helper to check if telemetry is enabled
function isTelemetryEnabled(): boolean {
  return process.env.REACT_APP_TELEMETRY_ENABLED !== 'false';
}

// Helper to log telemetry in debug mode
function debugLog(endpoint: string, payload: unknown) {
  if (process.env.REACT_APP_TELEMETRY_DEBUG === 'true') {
    console.log(`[Telemetry] ${endpoint}`, payload);
  }
}

// Helper to safely call telemetry (never throw)
async function safeTelemetryCall<T = unknown>(
  endpoint: string,
  payload: unknown,
  skipRecursion = false
): Promise<T | null> {
  if (!isTelemetryEnabled()) {
    console.log('[Telemetry] Disabled - skipping:', endpoint);
    return null;
  }

  debugLog(endpoint, payload);
  console.log('[Telemetry] Sending:', endpoint, payload);

  try {
    const response = await apiFetch<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    console.log('[Telemetry] Success:', endpoint, response);
    return response;
  } catch (error: unknown) {
    console.error('[Telemetry] Failed:', endpoint, error);
    // Prevent recursion: don't log telemetry errors back to telemetry
    if (!skipRecursion && !endpoint.includes('telemetry/error')) {
      // Silently fail for telemetry calls
      if (process.env.REACT_APP_TELEMETRY_DEBUG === 'true') {
        console.warn(`[Telemetry] Failed to send ${endpoint}:`, error);
      }
    }
    return null;
  }
}

// ===== Heartbeat & Online Users =====

export async function postHeartbeat(
  extra?: Partial<Omit<HeartbeatIn, 'origin'>>
): Promise<void> {
  await safeTelemetryCall('telemetry/heartbeat', {
    origin: ORIGIN,
    ...extra,
  });
}

export async function getOnlineUsers(): Promise<OnlineUsersOut | null> {
  if (!isTelemetryEnabled()) return null;

  try {
    const response = await apiFetch<OnlineUsersOut>('telemetry/online-users', {
      method: 'GET',
    });
    return response;
  } catch {
    return null;
  }
}

// ===== Error Tracking =====

export async function postError(
  code: string,
  message: string,
  context?: string
): Promise<void> {
  const payload: ErrorIn = {
    origin: ORIGIN,
    code,
    message,
    context,
  };
  await safeTelemetryCall('telemetry/error', payload, true);
}

// ===== Favorites =====

export async function postFavoriteAdd(product_id: number): Promise<void> {
  const payload: FavoriteAddIn = {
    origin: ORIGIN,
    product_id,
  };
  await safeTelemetryCall('telemetry/favorite-add', payload);
}

export async function postFavoriteRemove(product_id: number): Promise<void> {
  const payload: FavoriteRemoveIn = {
    origin: ORIGIN,
    product_id,
  };
  await safeTelemetryCall('telemetry/favorite-remove', payload);
}

export async function postFavoriteListView(): Promise<void> {
  const payload: FavoriteListViewIn = {
    origin: ORIGIN,
  };
  await safeTelemetryCall('telemetry/favorite-list-view', payload);
}

// ===== Jobs (Skin Analysis) =====

export async function postJobCreated(job_id: string): Promise<void> {
  const payload: JobCreatedIn = {
    origin: ORIGIN,
    job_id,
  };
  await safeTelemetryCall('telemetry/job-created', payload);
}

export async function postJobView(job_id: string): Promise<void> {
  const payload: JobViewIn = {
    origin: ORIGIN,
    job_id,
  };
  await safeTelemetryCall('telemetry/job-view', payload);
}

export async function postJobDeleted(job_id: string): Promise<void> {
  const payload: JobDeletedIn = {
    origin: ORIGIN,
    job_id,
  };
  await safeTelemetryCall('telemetry/job-deleted', payload);
}

export async function postJobCorrected(job_id: string): Promise<void> {
  const payload: JobCorrectedIn = {
    origin: ORIGIN,
    job_id,
  };
  await safeTelemetryCall('telemetry/job-corrected', payload);
}

// ===== Product Click & View =====

export async function postProductClick(
  product_keyword: string,
  product_link?: string
): Promise<void> {
  const payload: ProductClickIn = {
    origin: ORIGIN,
    product_keyword,
    product_link,
  };
  await safeTelemetryCall('telemetry/product-click', payload);
}

export async function postProductView(product_id: number): Promise<void> {
  const payload: ProductViewIn = {
    origin: ORIGIN,
    product_id,
  };
  await safeTelemetryCall('telemetry/product-view', payload);
}

// ===== Product Scanner =====

export async function postScannerStart(barcode?: string): Promise<void> {
  const payload: ScannerStartIn = {
    origin: ORIGIN,
    barcode,
  };
  await safeTelemetryCall('telemetry/product-scanner-start', payload);
}

export async function postScannerResultView(
  product_id?: number,
  barcode?: string
): Promise<void> {
  const payload: ScannerResultViewIn = {
    origin: ORIGIN,
    product_id,
    barcode,
  };
  await safeTelemetryCall('telemetry/product-scanner-result-view', payload);
}

export async function postScannerCompare(
  product_id_a: number,
  product_id_b: number
): Promise<void> {
  const payload: ScannerCompareIn = {
    origin: ORIGIN,
    product_id_a,
    product_id_b,
  };
  await safeTelemetryCall('telemetry/product-scanner-compare', payload);
}

export async function postScannerPrefsView(): Promise<void> {
  const payload: ScannerPrefsViewIn = {
    origin: ORIGIN,
  };
  await safeTelemetryCall('telemetry/product-scanner-preferences-view', payload);
}

export async function postScannerPrefsUpdate(
  changed_keys?: string[]
): Promise<void> {
  const payload: ScannerPrefsUpdateIn = {
    origin: ORIGIN,
    changed_keys,
  };
  await safeTelemetryCall('telemetry/product-scanner-preferences-update', payload);
}

// ===== Product Compatibility =====

export async function postCompatCompare(
  product_id_a: number,
  product_id_b: number
): Promise<void> {
  const payload: CompatCompareIn = {
    origin: ORIGIN,
    product_id_a,
    product_id_b,
  };
  await safeTelemetryCall('telemetry/product-compat-compare', payload);
}

export async function postCompatPrefsView(): Promise<void> {
  const payload: CompatPrefsViewIn = {
    origin: ORIGIN,
  };
  await safeTelemetryCall('telemetry/product-compat-preferences-view', payload);
}

export async function postCompatPrefsUpdate(
  changed_keys?: string[]
): Promise<void> {
  const payload: CompatPrefsUpdateIn = {
    origin: ORIGIN,
    changed_keys,
  };
  await safeTelemetryCall('telemetry/product-compat-preferences-update', payload);
}

// ===== Weather Analyzer =====

export async function postWeatherAnalyze(
  lat?: number,
  lon?: number,
  location?: string
): Promise<void> {
  const payload: WeatherAnalyzeIn = {
    origin: ORIGIN,
    lat,
    lon,
    location,
  };
  await safeTelemetryCall('telemetry/weather-analyze', payload);
}

// ===== User Plan =====

export async function postUserPlanChange(
  to_plan: string,
  from_plan?: string
): Promise<void> {
  const payload: UserPlanChangeIn = {
    origin: ORIGIN,
    to_plan,
    from_plan,
  };
  await safeTelemetryCall('telemetry/user-plan-change', payload);
}

